import axios from 'axios';
import get from 'lodash/get';
import { getJwtToken } from '../../services/auth';
import { forceLogoutDueToInvalidToken } from '../../services/forceLogoutDueToInvalidToken';
import { isInvalidAuthTokenError } from '../../services/sessionAuthError';

import { Amplify } from '@aws-amplify/core';
import config from '../../config';

// const token = localStorage.getItem('ap_idToken');
Amplify.configure(config);

const axiosConfig = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
 // baseURL: 'http://localhost:8000/data360',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: getJwtToken(),
    // appCode: 'PIH-WEB'
  },
});

const handleErroredResponse = async (error) => {
  const originalRequest = get(error, ['response', 'config'], undefined);
  const statusCode = get(error, ['response', 'status'], undefined);
  const errMessage = get(error, ['response', 'data', 'name'], undefined);
  const errData = get(error, ['response', 'data'], undefined);

  if (statusCode === 401) {
    if (errMessage === 'TokenExpiredError') {
      // remove token from local storage, if applicable
      // call for new id token
      // store token in token variable
      const token = await getJwtToken();
      if (token) {
        axios.defaults.headers.common['Authorization'] = token;
        return axiosConfig(originalRequest);
      }
      // Refresh failed — treat as invalid session
      await forceLogoutDueToInvalidToken();
      return Promise.reject(errData || error.message);
    }

    // Other 401s (e.g. Invalid token) — force re-login
    await forceLogoutDueToInvalidToken();
    return Promise.reject(errData || error.message);
  }

  if (isInvalidAuthTokenError(errData) || isInvalidAuthTokenError(error)) {
    await forceLogoutDueToInvalidToken();
  }

  if (errData) {
    return Promise.reject(errData);
  }
  return Promise.reject(error.message);
};

axiosConfig.interceptors.request.use(async (config) => {
  const aConfig = config;
  // call for id token and store in authToken variable
  const authToken = await getJwtToken();
  if (authToken) {
    aConfig.headers["Authorization"] = authToken;
  }
  return aConfig;
}, error => Promise.reject(error));

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

axiosConfig.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const statusCode = get(error, ['response', 'status'], undefined);
    const originalConfig = get(error, ['response', 'config'], undefined);
    const method = String(originalConfig?.method || '').toLowerCase();

    if (statusCode === 403 && originalConfig && MUTATING_METHODS.has(method)) {
      try {
        const { logAccessDeniedAudit } = await import(
          '../../features/project/services/auditTrailApi'
        );
        logAccessDeniedAudit(originalConfig.url || '', method.toUpperCase());
      } catch {
        /* non-blocking */
      }
    }

    return await handleErroredResponse(error);
  }
);

export default axiosConfig;
